import { action, computed } from 'mobx'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { Member } from '@/features/iam/members/Member'
import { type Opportunity } from './Opportunity'
import { formatWithLegendPriceUnit } from '@/features/jobs/legends/legend-price-unit'
import type { OpportunityDetailsSheetHeader } from '@/features/opportunities/components/OpportunityDetailsSheet'

/**
 * View model for an opportunity in the list.
 */
export class OpportunityViewModel {
  constructor(
    readonly opportunity: Opportunity,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _isPickupPending: () => boolean,
    private readonly _canPickUpOpportunity: (
      opportunity: Opportunity,
    ) => boolean,
    private readonly _getSelected: () => OpportunityViewModel | null,
    private readonly _select: (vm: OpportunityViewModel) => void,
  ) {}

  /**
   * The ID of the opportunity.
   */
  @computed
  get opportunityId() {
    return this.opportunity.id
  }

  /**
   * The names of the opportunity's scope items.
   */
  @computed
  get scopeItemsDescription() {
    return this.opportunity.scopeItems.map((i) => i.name).join(' • ')
  }

  /**
   * What the details sheet's header shows about the opportunity.
   */
  @computed
  get header(): OpportunityDetailsSheetHeader {
    return {
      workspaceName: this.opportunity.workspace.name,
      workspaceIcon: this.opportunity.workspace.branding.logos.icon,
      client: this.visibleClient,
      name: this.opportunity.name,
      description: this.scopeItemsDescription,
    }
  }

  /**
   * The opportunity's info lines.
   */
  @computed
  get infoLines() {
    return this.opportunity.infoLines.map((l) => ({
      title: l.title,
      content: l.content,
    }))
  }

  /**
   * The client, when the user may see who they are.
   */
  @computed
  get visibleClient() {
    return this.opportunity.client.canViewInfo
      ? this.opportunity.client
      : undefined
  }

  /**
   * Time left until a dispatch opportunity's pro is selected, or null.
   */
  @computed
  get selectionCountdown() {
    return this.opportunity.isDispatchOpportunity
      ? this.opportunity.timeUntilSelection
      : null
  }

  /**
   * The deadline, when it should be shown.
   */
  @computed
  get deadline() {
    return this.opportunity.shouldShowDeadline
      ? this.opportunity.dateDeadline
      : null
  }

  /**
   * The formatted provider cut, when it should be shown.
   */
  @computed
  get providerCutAmount() {
    return this.opportunity.providerCutAmount > 0
      ? formatWithLegendPriceUnit(
          this.opportunity.legend,
          this.opportunity.providerCutAmount,
        )
      : null
  }

  /**
   * The scope items of a bookkeeping opportunity, or null.
   */
  @computed
  get bookkeepingScopeItems() {
    return this.opportunity.hasBookkeeping
      ? this.opportunity.scopeItems.map((i) => ({
          name: i.name,
          quantity: i.quantity,
        }))
      : null
  }

  /**
   * The current member for the opportunity's workspace.
   */
  @computed
  get currentMember() {
    return (
      this._getCurrentUserWorkspaceMemberships().find(
        (m) => m.workspace === this.opportunity.workspace,
      )?.member ?? Member.none
    )
  }

  /**
   * Whether selection is disabled.
   */
  @computed
  get disabled() {
    return this._isPickupPending() || !this.canPickUp
  }

  /**
   * Whether the current member is allowed to pick up the opportunity
   * as themselves.
   */
  @computed
  get canPickUpAsMyself() {
    return (
      this.currentMember.permissions.jobPickup &&
      !this.opportunity.isDispatchOpportunity
    )
  }

  /**
   * Whether the current member is allowed to pick up the opportunity
   * given their job capacity in the workspace and the global capacity.
   */
  @computed
  get canPickUp() {
    return this._canPickUpOpportunity(this.opportunity)
  }

  /**
   * Whether this opportunity is the selected one.
   */
  @computed
  get isSelected() {
    return this._getSelected()?.opportunityId === this.opportunityId
  }

  /**
   * Selects this opportunity.
   */
  @action.bound
  select() {
    this._select(this)
  }
}
